import React, { useMemo, useCallback } from "react";
import { InspectionTypeMap } from "../../utils/enums";
import { areThereTransitDamages } from "../../utils/areThereTransitDamages";
import { emptyDamageGrid } from "../../utils/damageGrid";
import DamageChip from "../DamageChip/DamageChip";
import FrontDiagramImage from "./Images/FrontDiagramImage";
import RearDiagramImage from "./Images/RearDiagramImage";
import PassengerDiagramImage from "./Images/PassengerDiagramImage";
import DriverDiagramImage from "./Images/DriverDiagramImage";

import { DamageSide, DamageSeverity } from "../../utils/enums/index";

// Pre-existing damage first, then anything added in transit, each group
// ordered least to most severe. A missing inTransit flag counts as pre-existing.
const byTransitThenSeverity = (a, b) =>
  Number(a.inTransit === true) - Number(b.inTransit === true) ||
  (a.severity ?? 0) - (b.severity ?? 0);

const Inspection = ({ inspectionType, inspectionData, pickupDamages }) => {
  const title = useMemo(
    () => `${InspectionTypeMap[inspectionType]} Inspection`,
    [inspectionType],
  );

  const parsed = useMemo(() => {
    const images = emptyDamageGrid();

    const { damages = [] } = inspectionData ?? { damages: [] };
    for (const damage of damages || []) {
      // Skip anything that does not land in a known section rather than
      // throwing on an unexpected side/row/column and blanking the page
      const section = images[damage.side]?.[damage.row]?.[damage.column];

      if (section) {
        section.push(damage);
      }
    }

    for (const rows of Object.values(images)) {
      for (const columns of Object.values(rows)) {
        for (const section of Object.values(columns)) {
          section.sort(byTransitThenSeverity);
        }
      }
    }

    return images;
  }, [inspectionData]);

  const getDamageChips = useCallback(
    (side, row, column) => {
      const damageList = parsed[side][row][column];
      const hasDamages =
        Array.isArray(damageList) &&
        damageList.filter(damage => damage.severity !== DamageSeverity.None)
          .length > 0;

      if (!hasDamages) {
        return "No Damage";
      }

      return damageList.map((damage, index) => (
        <DamageChip key={damage.id ?? index} damage={damage} />
      ));
    },
    [parsed],
  );

  if (
    !areThereTransitDamages(
      inspectionType,
      inspectionData?.damages,
      pickupDamages,
    )
  ) {
    return (
      <div className="section-container inspection-section-print">
        <div className="section-header">{title}</div>

        <div className="info-box">
          <div>No new damage was reported</div>
        </div>
      </div>
    );
  }

  return (
    <div className="section-container inspection-section-print">
      <div className="section-header">{title}</div>

      <div className="diagrams-grid">
        <div className="diagram-grid-item">
          <div className="diagram-container">
            <div className="diagram-overlay">
              <div className="diagram-overlay__row row">
                <div className="diagram-overlay__section front-0-0">
                  <p className="diagram-overlay__section-number">1</p>
                </div>
                <div className="diagram-overlay__section front-0-1">
                  <p className="diagram-overlay__section-number">2</p>
                </div>
              </div>
              <div className="diagram-overlay__row row">
                <div className="diagram-overlay__section front-1-0">
                  <p className="diagram-overlay__section-number">3</p>
                </div>
                <div className="diagram-overlay__section front-1-1">
                  <p className="diagram-overlay__section-number">4</p>
                </div>
              </div>
            </div>

            <FrontDiagramImage />
          </div>

          <div className="diagram-damage-table-container">
            <div className="section-header section-header--extra-padding">
              Front
            </div>

            <div className="damage-table row">
              <div className="damage-table__col--labels col">
                <div className="damage-table__item damage-table__item--label">
                  1
                </div>
                <div className="damage-table__item damage-table__item--label">
                  2
                </div>
                <div className="damage-table__item damage-table__item--label">
                  3
                </div>
                <div className="damage-table__item damage-table__item--label">
                  4
                </div>
              </div>

              <div className="damage-table__col--data col">
                <div className="damage-table__item damage-table__item--data">
                  {getDamageChips(DamageSide.Front, 0, 0)}
                </div>
                <div className="damage-table__item damage-table__item--data">
                  {getDamageChips(DamageSide.Front, 0, 1)}
                </div>
                <div className="damage-table__item damage-table__item--data">
                  {getDamageChips(DamageSide.Front, 1, 0)}
                </div>
                <div className="damage-table__item damage-table__item--data">
                  {getDamageChips(DamageSide.Front, 1, 1)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="diagram-grid-item">
          <div className="diagram-container">
            <div className="diagram-overlay">
              <div className="diagram-overlay__row row">
                <div className="diagram-overlay__section rear-0-0">
                  <p className="diagram-overlay__section-number">1</p>
                </div>
                <div className="diagram-overlay__section rear-0-1">
                  <p className="diagram-overlay__section-number">2</p>
                </div>
              </div>
              <div className="diagram-overlay__row row">
                <div className="diagram-overlay__section rear-1-0">
                  <p className="diagram-overlay__section-number">3</p>
                </div>
                <div className="diagram-overlay__section rear-1-1">
                  <p className="diagram-overlay__section-number">4</p>
                </div>
              </div>
            </div>

            <RearDiagramImage />
          </div>

          <div className="diagram-damage-table-container">
            <div className="section-header section-header--extra-padding">
              Rear
            </div>

            <div className="damage-table row">
              <div className="damage-table__col--labels col">
                <div className="damage-table__item damage-table__item--label">
                  1
                </div>
                <div className="damage-table__item damage-table__item--label">
                  2
                </div>
                <div className="damage-table__item damage-table__item--label">
                  3
                </div>
                <div className="damage-table__item damage-table__item--label">
                  4
                </div>
              </div>

              <div className="damage-table__col--data col">
                <div className="damage-table__item damage-table__item--data">
                  {getDamageChips(DamageSide.Rear, 0, 0)}
                </div>
                <div className="damage-table__item damage-table__item--data">
                  {getDamageChips(DamageSide.Rear, 0, 1)}
                </div>
                <div className="damage-table__item damage-table__item--data">
                  {getDamageChips(DamageSide.Rear, 1, 0)}
                </div>
                <div className="damage-table__item damage-table__item--data">
                  {getDamageChips(DamageSide.Rear, 1, 1)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="diagram-grid-item">
          <div className="diagram-container">
            <div className="diagram-overlay">
              <div className="diagram-overlay__row row">
                <div className="diagram-overlay__section driverside-0-0">
                  <p className="diagram-overlay__section-number">1</p>
                </div>
                <div className="diagram-overlay__section driverside-0-1">
                  <p className="diagram-overlay__section-number">2</p>
                </div>
                <div className="diagram-overlay__section driverside-0-2">
                  <p className="diagram-overlay__section-number">3</p>
                </div>
                <div className="diagram-overlay__section driverside-0-3">
                  <p className="diagram-overlay__section-number">4</p>
                </div>
              </div>
              <div className="diagram-overlay__row row">
                <div className="diagram-overlay__section driverside-1-0">
                  <p className="diagram-overlay__section-number">5</p>
                </div>
                <div className="diagram-overlay__section driverside-1-1">
                  <p className="diagram-overlay__section-number">6</p>
                </div>
                <div className="diagram-overlay__section driverside-1-2">
                  <p className="diagram-overlay__section-number">7</p>
                </div>
                <div className="diagram-overlay__section driverside-1-3">
                  <p className="diagram-overlay__section-number">8</p>
                </div>
              </div>
            </div>

            <DriverDiagramImage />
          </div>

          <div className="diagram-damage-table-container">
            <div className="section-header section-header--extra-padding">
              Driver Side
            </div>

            <div className="damage-table row">
              <div className="damage-table__col--labels col">
                <div className="damage-table__item damage-table__item--label">
                  1
                </div>
                <div className="damage-table__item damage-table__item--label">
                  2
                </div>
                <div className="damage-table__item damage-table__item--label">
                  3
                </div>
                <div className="damage-table__item damage-table__item--label">
                  4
                </div>
                <div className="damage-table__item damage-table__item--label">
                  5
                </div>
                <div className="damage-table__item damage-table__item--label">
                  6
                </div>
                <div className="damage-table__item damage-table__item--label">
                  7
                </div>
                <div className="damage-table__item damage-table__item--label">
                  8
                </div>
              </div>

              <div className="damage-table__col--data col">
                <div className="damage-table__item damage-table__item--data">
                  {getDamageChips(DamageSide.DriverSide, 0, 0)}
                </div>
                <div className="damage-table__item damage-table__item--data">
                  {getDamageChips(DamageSide.DriverSide, 0, 1)}
                </div>
                <div className="damage-table__item damage-table__item--data">
                  {getDamageChips(DamageSide.DriverSide, 0, 2)}
                </div>
                <div className="damage-table__item damage-table__item--data">
                  {getDamageChips(DamageSide.DriverSide, 0, 3)}
                </div>
                <div className="damage-table__item damage-table__item--data">
                  {getDamageChips(DamageSide.DriverSide, 1, 0)}
                </div>
                <div className="damage-table__item damage-table__item--data">
                  {getDamageChips(DamageSide.DriverSide, 1, 1)}
                </div>
                <div className="damage-table__item damage-table__item--data">
                  {getDamageChips(DamageSide.DriverSide, 1, 2)}
                </div>
                <div className="damage-table__item damage-table__item--data">
                  {getDamageChips(DamageSide.DriverSide, 1, 3)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="diagram-grid-item">
          <div className="diagram-container">
            <div className="diagram-overlay">
              <div className="diagram-overlay__row row">
                <div className="diagram-overlay__section passengerside-0-0">
                  <p className="diagram-overlay__section-number">1</p>
                </div>
                <div className="diagram-overlay__section passengerside-0-1">
                  <p className="diagram-overlay__section-number">2</p>
                </div>
                <div className="diagram-overlay__section passengerside-0-2">
                  <p className="diagram-overlay__section-number">3</p>
                </div>
                <div className="diagram-overlay__section passengerside-0-3">
                  <p className="diagram-overlay__section-number">4</p>
                </div>
              </div>
              <div className="diagram-overlay__row row">
                <div className="diagram-overlay__section passengerside-1-0">
                  <p className="diagram-overlay__section-number">5</p>
                </div>
                <div className="diagram-overlay__section passengerside-1-1">
                  <p className="diagram-overlay__section-number">6</p>
                </div>
                <div className="diagram-overlay__section passengerside-1-2">
                  <p className="diagram-overlay__section-number">7</p>
                </div>
                <div className="diagram-overlay__section passengerside-1-3">
                  <p className="diagram-overlay__section-number">8</p>
                </div>
              </div>
            </div>

            <PassengerDiagramImage />
          </div>

          <div className="diagram-damage-table-container">
            <div className="section-header section-header--extra-padding">
              Passenger Side
            </div>

            <div className="damage-table row">
              <div className="damage-table__col--labels col">
                <div className="damage-table__item damage-table__item--label">
                  1
                </div>
                <div className="damage-table__item damage-table__item--label">
                  2
                </div>
                <div className="damage-table__item damage-table__item--label">
                  3
                </div>
                <div className="damage-table__item damage-table__item--label">
                  4
                </div>
                <div className="damage-table__item damage-table__item--label">
                  5
                </div>
                <div className="damage-table__item damage-table__item--label">
                  6
                </div>
                <div className="damage-table__item damage-table__item--label">
                  7
                </div>
                <div className="damage-table__item damage-table__item--label">
                  8
                </div>
              </div>

              <div className="damage-table__col--data col">
                <div className="damage-table__item damage-table__item--data">
                  {getDamageChips(DamageSide.PassengerSide, 0, 0)}
                </div>
                <div className="damage-table__item damage-table__item--data">
                  {getDamageChips(DamageSide.PassengerSide, 0, 1)}
                </div>
                <div className="damage-table__item damage-table__item--data">
                  {getDamageChips(DamageSide.PassengerSide, 0, 2)}
                </div>
                <div className="damage-table__item damage-table__item--data">
                  {getDamageChips(DamageSide.PassengerSide, 0, 3)}
                </div>
                <div className="damage-table__item damage-table__item--data">
                  {getDamageChips(DamageSide.PassengerSide, 1, 0)}
                </div>
                <div className="damage-table__item damage-table__item--data">
                  {getDamageChips(DamageSide.PassengerSide, 1, 1)}
                </div>
                <div className="damage-table__item damage-table__item--data">
                  {getDamageChips(DamageSide.PassengerSide, 1, 2)}
                </div>
                <div className="damage-table__item damage-table__item--data">
                  {getDamageChips(DamageSide.PassengerSide, 1, 3)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Inspection;
